import type { RootState } from '@/store';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';

import type { FeeItem } from '@/apis/extended/admin/interestRateApi';
import {
  computeMergedFeeBreakdown,
  type CalculatedFeeRule,
} from '@/utils/fees/feeEngine';
import { ProductEsitmation } from '@/types/ProductType';
import { StackedFeeItem } from './StackedFeeItem';

type ProductDetailClosingCostsProps = {
  estimation: ProductEsitmation;
};

export const ProductDetailClosingCosts = ({
  estimation,
}: ProductDetailClosingCostsProps) => {
  const theme = useTheme();
  const feeItemsByLoanType = useSelector(
    (state: RootState) => (state.interestRate as any).feeItemsByLoanType
  ) as Partial<Record<string, FeeItem[]>>;
  const calculatedFeeRulesByLoanType = useSelector(
    (state: RootState) =>
      (state.interestRate as any).calculatedFeeRulesByLoanType
  ) as Partial<Record<string, CalculatedFeeRule[]>>;

  if (!estimation) return null;

  const loanTypeMap: Record<string, string> = {
    conventional: 'Conventional',
    fha: 'FHA',
    va: 'VA',
    usda: 'USDA',
    dscr: 'DSCR',
    commercial: 'Commercial',
    fix_and_flip: 'FixAndFlip',
    bridge: 'Bridge',
    heloc: 'HELOC',
    heloan: 'HELOAN',
    second_mortgage: 'SecondMortgage',
    cash: 'Cash',
    default: 'Conventional',
  };
  const loanType =
    loanTypeMap[((estimation as { type?: string }).type || '').toLowerCase()] ||
    'Conventional';
  const feeItems = feeItemsByLoanType?.[loanType] || [];
  const calculatedFeeRules = calculatedFeeRulesByLoanType?.[loanType] || [];
  const breakdown = computeMergedFeeBreakdown({
    estimation,
    calculatedFeeRules,
    feeItems,
    loanAmount: (estimation as { loanAmount?: number }).loanAmount || 0,
  });

  const SectionHeader = ({ title }: { title: string }) => (
    <Box
      sx={{
        mt: 3,
        mb: 2,
        pt: 2,
        border: `1px solid ${theme.palette.greyShades.border1}`,
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 },
        background: alpha(theme.palette.primary.main, 0.04),
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, ${alpha(
            theme.palette.primary.main,
            0.35
          )} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          borderRadius: '8px 8px 0 0',
        },
      }}
    >
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{
          color: theme.palette.text.primary,
          fontSize: { xs: '1rem', sm: '1.125rem' },
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <Card
      sx={{
        background: alpha(theme.palette.primary.main, 0.03),
        color: theme.palette.text.primary,
        borderRadius: 2,
        border: `1px solid ${theme.palette.greyShades.border1}`,
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <SectionHeader title="Lender Fees" />
            {breakdown.byCategory.lender.map((fee) => (
              <StackedFeeItem
                key={fee.key}
                label={fee.label}
                value={fee.computedAmount}
                description={fee.description}
                adminConfigured={
                  !fee.description && !fee.key.startsWith('calc_')
                }
              />
            ))}

            <SectionHeader title="Government Fees" />
            {breakdown.byCategory.government.map((fee) => (
              <StackedFeeItem
                key={fee.key}
                label={fee.label}
                value={fee.computedAmount}
                description={fee.description}
                adminConfigured={
                  !fee.description && !fee.key.startsWith('calc_')
                }
              />
            ))}
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionHeader title="Title Fees" />
            {breakdown.byCategory.title.map((fee) => (
              <StackedFeeItem
                key={fee.key}
                label={fee.label}
                value={fee.computedAmount}
                description={fee.description}
                adminConfigured={
                  !fee.description && !fee.key.startsWith('calc_')
                }
              />
            ))}

            <SectionHeader title="Prepaid Costs" />
            {breakdown.byCategory.prepaid.map((fee) => (
              <StackedFeeItem
                key={fee.key}
                label={fee.label}
                value={fee.computedAmount}
                description={fee.description}
                adminConfigured={
                  !fee.description && !fee.key.startsWith('calc_')
                }
              />
            ))}

            {breakdown.byCategory.other.length > 0 && (
              <>
                <SectionHeader title="Other" />
                {breakdown.byCategory.other.map((fee) => (
                  <StackedFeeItem
                    key={fee.key}
                    label={fee.label}
                    value={fee.computedAmount}
                    description={fee.description}
                    adminConfigured={
                      !fee.description && !fee.key.startsWith('calc_')
                    }
                  />
                ))}
              </>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
